'use client';

import { c as _c } from "react/compiler-runtime";
import type { Time } from '@morpho-org/morpho-ts';
import type { PropsChainIdMarketId } from '@morpho-org/ui-v6/types';
import { MathLib } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { TEN } from '@morpho-org/dapp-core';
import { InfoTooltip, Select } from '@morpho-org/ui-v6/client';
import { Card, Skeleton } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { SegmentedControlItem, SegmentedControlList, Tabs, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { keepPreviousData, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { formatUnits } from 'viem';
import type { MarketHistory } from '@/lib/procedures/marketHistory.contract';
import SimpleChart from '@/components/charts/components/SimpleChart';
import { formatTimeseriesData } from '@/components/charts/helpers/highcharts';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { defaultTimeFrame, timeFrameOptions } from '@/config/table';
import { splitValueAndUnit } from '@/helpers/number';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { marketHistoryQueryOptions, toHistoryRange } from './totalBorrow.query';
export type ChartTotalBorrowClientProps = PropsChainIdMarketId;
type ChartMode = 'Borrow' | 'Supply' | 'Liquidity';
const CHART_TITLES: Record<ChartMode, string> = {
  Borrow: 'Outstanding Loans',
  Supply: 'Total Supply',
  Liquidity: 'Market Liquidity'
};
const modeOptions: {
  value: ChartMode;
  label: string;
}[] = [{
  value: 'Borrow',
  label: 'Borrow'
}, {
  value: 'Supply',
  label: 'Supply'
}, {
  value: 'Liquidity',
  label: 'Liquidity'
}];
const AMOUNT_FIELDS = {
  Borrow: 'borrowAssets',
  Supply: 'supplyAssets',
  Liquidity: 'liquidityAssets'
} as const satisfies Record<ChartMode, string>;
const USD_FIELDS = {
  Borrow: 'borrowAssetsUsd',
  Supply: 'supplyAssetsUsd',
  Liquidity: 'liquidityAssetsUsd'
} as const satisfies Record<ChartMode, string>;
const USD = 'USD';
const toAmount = (raw: string | null, decimals: number) => raw === null ? null : Number(formatUnits(BigInt(raw), decimals));
type HistoryPoint = MarketHistory['points'][number];

/**
 * The picked series. A history with no point charts nothing, so the chart shows its no-data state;
 * points without a single USD value mean the prices are missing, which shows the chart's error
 * instead of a flat line.
 */
function toChartSeries(points: readonly HistoryPoint[], mode: ChartMode, isUsd: boolean, decimals: number) {
  const series = formatTimeseriesData(points.map(point => ({
    x: point.timestamp,
    y: isUsd ? point[USD_FIELDS[mode]] : toAmount(point[AMOUNT_FIELDS[mode]], decimals)
  })));
  const hasValue = series.some(([, y]) => y !== null);
  return {
    formattedData: hasValue ? series : [],
    usdMissing: isUsd && points.length > 0 && !hasValue
  };
}

/**
 * The market's outstanding loans, total supply or liquidity, now and over the picked timeframe, in
 * USD or in the loan token. The headline is the latest point of the default timeframe, the one the
 * server hydrates, valued at the loan token's spot price. Another timeframe keeps the previous one
 * on screen in the chart's loading state until it arrives, and a failed one shows the chart's
 * error with the selectors still usable, as do points without any USD value in USD. A history with
 * no point shows the no-data state, and a missing spot price a "-" headline. A
 * `partial` history still charts the points it has, and marks the card with `data-partial`.
 */
const ChartTotalBorrowClient = t0 => {
  const $ = _c(79);
  const {
    chainId,
    marketId
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  const isMobile = useIsMobile();
  const [timeFrame, setTimeFrame] = useState(defaultTimeFrame);
  const [mode, setMode] = useState("Borrow");
  const [currency, setCurrency] = useState(USD);
  const isUsd = currency === USD;
  let t1;
  if ($[0] !== chainId || $[1] !== marketId) {
    t1 = marketHistoryQueryOptions({
      chainId,
      marketId,
      range: toHistoryRange(defaultTimeFrame)
    });
    $[0] = chainId;
    $[1] = marketId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: latest
  } = useSuspenseQuery(t1);
  const {
    loanToken
  } = latest;
  const symbol = loanToken.symbol ?? "Asset";
  let t2;
  if ($[3] !== symbol) {
    t2 = {
      value: symbol,
      label: symbol
    };
    $[3] = symbol;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      value: USD,
      label: USD
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2) {
    t4 = [t2, t3];
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const currencyOptions = t4;
  let t5;
  if ($[8] !== chainId || $[9] !== marketId || $[10] !== timeFrame) {
    t5 = marketHistoryQueryOptions({
      chainId,
      marketId,
      range: toHistoryRange(timeFrame)
    });
    $[8] = chainId;
    $[9] = marketId;
    $[10] = timeFrame;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = {
      ...t5,
      placeholderData: keepPreviousData
    };
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    data,
    isError,
    isPlaceholderData,
    isPending
  } = useQuery(t6);
  const loading = isPending || isPlaceholderData && !isError;
  let t7;
  if ($[14] !== data?.points) {
    t7 = data?.points ?? [];
    $[14] = data?.points;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isUsd || $[17] !== loanToken.decimals || $[18] !== mode || $[19] !== t7) {
    t8 = toChartSeries(t7, mode, isUsd, loanToken.decimals);
    $[16] = isUsd;
    $[17] = loanToken.decimals;
    $[18] = mode;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const {
    formattedData,
    usdMissing
  } = t8;
  let formattedValue;
  let latestUsd;
  let t9;
  if ($[21] !== isUsd || $[22] !== latest.points || $[23] !== loanToken.decimals || $[24] !== loanToken.priceUsd || $[25] !== loanToken.symbol || $[26] !== mode) {
    const latestRaw = latest.points.at(-1)?.[AMOUNT_FIELDS[mode]];
    const latestAmount = latestRaw == null ? undefined : BigInt(latestRaw);
    latestUsd = latestAmount === undefined || loanToken.priceUsd === null ? undefined : MathLib.mulDivDown(latestAmount, safeParseNumber(loanToken.priceUsd), TEN ** BigInt(loanToken.decimals));
    const formatValue = formatter => isUsd ? formatter.default("-").digits(2).of(latestUsd, 18) : formatter.default("-").digits(2).of(latestAmount, loanToken.decimals);
    formattedValue = splitValueAndUnit(formatValue(formatters.short));
    t9 = isUsd ? formatters.commas.default("-").digits(2).unit("$").of(latestUsd, 18) : formatters.commas.default("-").digits(2).unit(loanToken.symbol ?? undefined).of(latestAmount, loanToken.decimals);
    $[21] = isUsd;
    $[22] = latest.points;
    $[23] = loanToken.decimals;
    $[24] = loanToken.priceUsd;
    $[25] = loanToken.symbol;
    $[26] = mode;
    $[27] = formattedValue;
    $[28] = latestUsd;
    $[29] = t9;
  } else {
    formattedValue = $[27];
    latestUsd = $[28];
    t9 = $[29];
  }
  const detailedFormattedValue = t9;
  const t10 = data?.partial ? "true" : undefined;
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      lineHeight: "normal"
    };
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const t12 = CHART_TITLES[mode];
  let t13;
  if ($[31] !== currency || $[32] !== t12) {
    t13 = <span className="text-secondary-foreground text-xs" style={t11}>{t12} ({currency})</span>;
    $[31] = currency;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== mode) {
    t14 = mode === "Liquidity" && <InfoTooltip tooltipContent={<>The existing liquidity on this market. Does not account for the Public Allocator.</>} />;
    $[34] = mode;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t13 || $[37] !== t14) {
    t15 = <div className="flex min-h-[20px] items-center gap-[2px]">{t13}{t14}</div>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== isMobile || $[40] !== mode || $[41] !== timeFrame) {
    t16 = isMobile && <div className="gap-sm flex flex-wrap items-center"><Select options={modeOptions} value={mode} onChange={setMode} /><Select options={timeFrameOptions} value={timeFrame} onChange={setTimeFrame} size="default" /></div>;
    $[39] = isMobile;
    $[40] = mode;
    $[41] = timeFrame;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== detailedFormattedValue || $[44] !== formattedValue.unit || $[45] !== formattedValue.value || $[46] !== isUsd || $[47] !== latestUsd) {
    t17 = formattedValue.value ? <Tooltip><TooltipTrigger asChild={true}><div className="flex w-[fit-content] cursor-default items-center gap-[2px]" data-testid="total-borrow-value"><span className="text-title-sm">{isUsd && latestUsd !== undefined ? "$" : ""}{formattedValue.value}</span><span className="text-title-sm text-muted-foreground">{formattedValue.unit}</span></div></TooltipTrigger>{!!detailedFormattedValue && <TooltipContent side="right">{detailedFormattedValue}</TooltipContent>}</Tooltip> : <Skeleton className="h-(--text-title-sm--line-height) w-[130px] rounded-[20px]" />;
    $[43] = detailedFormattedValue;
    $[44] = formattedValue.unit;
    $[45] = formattedValue.value;
    $[46] = isUsd;
    $[47] = latestUsd;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t15 || $[50] !== t16 || $[51] !== t17) {
    t18 = <div className="gap-xs flex flex-col">{t15}{t16}{t17}</div>;
    $[49] = t15;
    $[50] = t16;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== currency || $[54] !== currencyOptions || $[55] !== isMobile || $[56] !== mode || $[57] !== timeFrame) {
    t19 = !isMobile && <div className="gap-sm flex flex-wrap items-center"><Tabs value={mode} onValueChange={value => setMode(value as ChartMode)}><SegmentedControlList size="default" aria-label="Metric">{modeOptions.map(_temp)}</SegmentedControlList></Tabs><Tabs value={currency} onValueChange={setCurrency}><SegmentedControlList size="default" aria-label="Currency">{currencyOptions.map(_temp2)}</SegmentedControlList></Tabs><Select options={timeFrameOptions} value={timeFrame} onChange={setTimeFrame} size="default" /></div>;
    $[53] = currency;
    $[54] = currencyOptions;
    $[55] = isMobile;
    $[56] = mode;
    $[57] = timeFrame;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== t18 || $[60] !== t19) {
    t20 = <div className="gap-sm flex flex-wrap items-center justify-between">{t18}{t19}</div>;
    $[59] = t18;
    $[60] = t19;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  const t21 = isError || !loading && usdMissing;
  let t22;
  if ($[62] !== loading || $[63] !== t21) {
    t22 = {
      loading,
      error: t21
    };
    $[62] = loading;
    $[63] = t21;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  const t23 = isUsd ? "$" : currency;
  const t24 = CHART_TITLES[mode];
  let t25;
  if ($[65] !== formattedData || $[66] !== palette.areaChartColors[0] || $[67] !== palette.colors[0] || $[68] !== t24) {
    t25 = [{
      type: "areaspline",
      name: t24,
      data: formattedData,
      color: palette.colors[0],
      lineColor: palette.colors[0],
      fillColor: palette.areaChartColors[0]
    }];
    $[65] = formattedData;
    $[66] = palette.areaChartColors[0];
    $[67] = palette.colors[0];
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    };
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== t22 || $[72] !== t23 || $[73] !== t25) {
    t27 = <SimpleChart statuses={t22} unit={t23} height="224px" series={t25} additionalOptions={t26} />;
    $[71] = t22;
    $[72] = t23;
    $[73] = t25;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== t10 || $[76] !== t20 || $[77] !== t27) {
    t28 = <Card className="gap-lg py-lg px-md md:px-lg w-[100%] flex-col" data-testid="total-borrow-chart-card" data-partial={t10} withBorder={true}>{t20}{t27}</Card>;
    $[75] = t10;
    $[76] = t20;
    $[77] = t27;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  return t28;
};
export default ChartTotalBorrowClient;
function _temp(option) {
  return <SegmentedControlItem key={option.value} value={option.value}>{option.label}</SegmentedControlItem>;
}
function _temp2(option_0) {
  return <SegmentedControlItem key={option_0.value} value={option_0.value}>{option_0.label}</SegmentedControlItem>;
}